/* eslint react/prop-types: off */

import React, { useEffect, useState } from 'react'
import { Alert, Button, Icon, Popup } from 'camino-react'
import { map, isArray, each, filter, isNil } from 'lodash'

import { cloudhelpGet, cloudhelpPost } from 'Utilities/CloudHelp'

import Attachments from './Attachments'
import Information from './Information'
import Timeline from './Timeline'
import FreeFormSections from './FreeFormSections'
import Participants from './Participants'
import CSITasks from './CSITasks'

const IncidentReport = ({
  onClose,
  ticketID,
  onSuccess,
  currentUser,
  problemCSITasks,
  policy
}) => {
  const INCIDENT_REPORT_URL = `/api/tickets/${ticketID}/incident_reports`

  const [report, setReport] = useState({})
  const [error, setError] = useState()

  // There is no different way to add classes to Popup wrapper
  useEffect(() => {
    const popup = document.querySelector('.popup-overlay')
    if (popup) popup.classList.add('overflow-y-scroll', 'py-20')
  }, [ticketID])

  useEffect(() => {
    if (ticketID) {
      cloudhelpGet(INCIDENT_REPORT_URL)
        .then(({ data }) => { setReport(data) })
    }
  }, [ticketID])

  const closeModal = () => {
    setError(null)
    setReport({})
    onClose()
  }

  const save = (afterSave = () => null) => {
    const {
      comments,
      introduction,
      incidentTriggers,
      executiveSummary,
      participants,
      csiTasks,
      resolutionConfirmation,
      monitoringReport,
      attachments,
      attachmentsToRemove
    } = report

    const data = {
      ticket_id: '',
      comments,
      introduction,
      incident_triggers: incidentTriggers,
      executive_summary: executiveSummary,
      resolution_confirmation: resolutionConfirmation,
      monitoring_report: monitoringReport,
      incident_report_participants: map(participants, ({ id, role }) => ({ user_id: id, role })),
      csi_tasks: map(csiTasks, ({ id }) => ({ csi_task_id: id })),
      attachments_to_remove: attachmentsToRemove
    }

    const formData = new FormData()
    each(data, (value, key) => {
      if (isArray(value)) {
        each(value, (val, ke) => each(val, (v, k) => formData.append(`${key}[][${k}]`, v)))
      } else {
        formData.append(key, value)
      }
    })
    each(filter(attachments, att => !isNil(att.size)), attachment => formData.append('attachments[]', attachment))

    cloudhelpPost(INCIDENT_REPORT_URL, formData)
      .then(() => {
        closeModal()
        onSuccess('Incident Report has been successfully saved!')
        afterSave()
      })
      .catch(request => setError(request.response.data.error))
  }

  const openPDF = () => {
    window.open(`/tickets/${ticketID}/incident_reports.pdf`, '_blank').focus()
  }

  const informationSectionFields = [
    { name: 'Client Name', value: report.clientName },
    { name: 'Client Contact', value: report.clientContactName },
    { name: 'Priority', value: report.priority },
    { name: 'Title', value: report.title },
    { name: 'Ticket ID', value: report.ticketID },
    { name: 'Problem ID', value: report.problemID },
    { name: 'Problem Manager', value: report.problemManager }
  ]

  const canEdit = policy('problem', 'incident_report', 'edit')

  return (
    <Popup
      modal
      open={ticketID}
      closeOnDocumentClick={false}
      contentStyle={{ borderRadius: 8 }}
    >
      <div className="text-gray-600 p-10">
        <div className="flex justify-between mb-10">
          <h2 className="m-0 font-medium">Incident Report</h2>
          <button
            className="text-4xl font-semibold duration-300 hover:bg-gray-200 w-12 h-12 flex items-center justify-center rounded-full cursor-pointer"
            onClick={closeModal}
            type="button"
          >
            <Icon type="cross" />
          </button>
        </div>

        <h3 className="m-0 mb-4">Information</h3>
        <Information
          report={report}
          fieldsToRender={informationSectionFields}
        />

        {error && <Alert type="error">{error}</Alert>}

        <FreeFormSections
          report={report}
          setReport={data => setReport(data)}
          fieldsToRender={[
            { label: 'Executive Summary', key: 'executiveSummary' },
            { label: 'Incident Triggers', key: 'incidentTriggers' }
          ]}
          canEdit={canEdit}
        />
        <Information
          fieldsToRender={[
            { name: 'Reported By', value: report.problemCreator },
            { name: 'Business Impact', value: report.businessImpact },
            { name: 'Root Cause', value: report.rootCause },
            { name: 'Corrective Actions', value: report.correctiveAction }]}
        />
        <FreeFormSections
          report={report}
          setReport={data => setReport(data)}
          fieldsToRender={[
            { label: 'Resolution Confirmation', key: 'resolutionConfirmation' },
            { label: 'Monitoring Report', key: 'monitoringReport' }
          ]}
          canEdit={canEdit}
        />

        <h3 className="m-0 mb-4">Participants</h3>
        <Participants report={report} setReport={data => setReport(data)} canEdit={canEdit} />

        <Timeline comments={report.comments} report={report} currentUser={currentUser} setReport={data => setReport(data)} canEdit={canEdit} />

        <h3 className="m-0 mb-6 mt-4">References</h3>
        <div key="attachments" className="content-section__content">
          <Attachments
            setReport={data => setReport(data)}
            report={report}
            canEdit={canEdit}
          />
        </div>

        <h3 className="m-0 mb-2 mt-4">CSI Tasks</h3>
        <CSITasks report={report} setReport={data => setReport(data)} problemTasks={problemCSITasks} canEdit={canEdit} />

        <div className="flex justify-center mt-8">
          {policy('problem', 'incident_report', 'edit') && (
            <>
              <Button onClick={save}>Save</Button>
              <div className="mr-4" />
            </>
          )}
          {policy('problem', 'incident_report', 'pdf') && (
            <Button onClick={() => save(openPDF)}>
              Save & Open PDF
            </Button>
          )}
        </div>
      </div>
    </Popup>
  )
}

export default IncidentReport
